// Which service categories can be booked in the logged-in app.
//
// Admins pause and resume services per country, with optional city overrides, in
// the admin console (App Settings > Service Availability; backend
// SERVICE_AVAILABILITY, GET /public/GetServiceAvailability). Components read them
// through useServiceAvailability() (utils/useServiceAvailability.js), which hands
// back the helpers below bound to the market's settings. The same settings make
// the chat-support AI answer questions about a paused service with its message.
//
// SERVICE_AVAILABILITY below is only the fallback: what applies while the
// settings load and if the API can't be reached. It matches the values seeded in
// the backend migration, so a failed fetch doesn't reopen a paused service.
//
// Options per category (keys are the requestType values):
//   showOnHome      - false hides the category's button on the /home screen
//                     (default true; the button also needs the category to exist
//                     in requestCategorySettings).
//   paused          - true: requests can still be submitted, but they cannot be
//                     confirmed. The cost card shows `pausedMessage` instead of
//                     Confirm Service.
//   pausedSubTypes  - pause only these sub-types, e.g. ['Plumbing'] for handyman
//                     or ['Babysitter'] for helper. The rest stay bookable. Handyman
//                     sub-types are the first booking question: "Electrical",
//                     "Plumbing", "Door, cupboard/cabinet".
//   pausedMessage   - shown wherever a paused service would otherwise be booked.
//   paymentFlow     - helper only: 'PREPAY' pays at booking through /booking/<id>
//                     (one-time: card or PayNow; weekly plans: the first visit by
//                     card, saved for the next visits, with the 4-visit minimum -
//                     utils/recurring-plan.js). 'LEGACY' (default) is the
//                     card hold / pay-after-service flow.

const REVAMP_MESSAGE = (segment) =>
    `Our ${segment} services segment is undergoing a major revamp, which may take several weeks to be resumed. Apologies for any inconvenience caused.`

export const SERVICE_AVAILABILITY = {
    'Aircon Services': {
        showOnHome: true,
    },
    'Fitness Trainer': {
        showOnHome: true,
    },
    'Domestic Services': {
        showOnHome: true,
        // Fallback when the API has no row: the whole helper service paused
        paused: true,
        // Blocked at the date/time step before a request is even created. Only
        // "Helper" reopens in the Nov 2026 relaunch. Deep-clean types are the
        // first booking question (key 0) and helper types the second (key 1);
        // DomesticServiceDateTime checks both, because unpausing Domestic would
        // otherwise reopen spring and move-in/out cleaning too.
        pausedSubTypes: ['Elderly Care', 'Cooking Assistance', 'Babysitter',
            'Spring cleaning', 'Move in move out cleaning', 'Post renovation'],
        pausedMessage: REVAMP_MESSAGE('helper'),
    },
    'Handyman Services': {
        showOnHome: true,
        paused: true,
        // Only Electrical is open. Values are the key-1 service types from
        // utils/handyman-quickbook.js.
        pausedSubTypes: ['Plumbing', 'Door, cupboard/cabinet', 'Drilling & Installation', 'Painting'],
        pausedMessage: REVAMP_MESSAGE('handyman'),
    },
}

// A handyman request's sub-type is the answer to the first booking question,
// stored as { 1: "Electrical", criteria: "Handyman service type - Electrical" }.
// The quick-book flows (HandymanBooking, HandymanDealItems,
// /home/services/handyman) and the question wizard (ServiceCriteria) both store
// it under key "1". The criteria text is the fallback.
export const getHandymanSubType = (request) => {
    for (const c of request?.criteria || []) {
        if (typeof c?.['1'] === 'string') return c['1']
        const match = /Handyman service type - (.+)$/i.exec(c?.criteria || '')
        if (match) return match[1].trim()
    }
    return null
}

/**
 * The helpers for one market's settings ({ [requestType]: options }, as above).
 * A request type with no entry is available and shown on home.
 */
export const availabilityHelpers = (availability = SERVICE_AVAILABILITY) => {
    const settingsFor = (requestType) => availability[requestType] || {}

    const isShownOnHome = (requestType) => settingsFor(requestType).showOnHome !== false

    const getPausedMessage = (requestType) =>
        settingsFor(requestType).pausedMessage || REVAMP_MESSAGE('service')

    const isSubTypePaused = (requestType, subType) =>
        (settingsFor(requestType).pausedSubTypes || []).includes(subType)

    const getPaymentFlow = (requestType) =>
        settingsFor(requestType).paymentFlow === 'PREPAY' ? 'PREPAY' : 'LEGACY'

    /**
     * Whether a submitted request may be confirmed and paid.
     * @param {object} request  the request, including criteria and serviceCosts
     */
    const canConfirmRequest = (request) => {
        const settings = settingsFor(request?.requestType)

        // Paused means paused for every booking, paid in advance or not
        // (the backend's ServiceAvailabilityService.refusal does the same)
        if (settings.paused) return false

        if (request?.requestType === 'Handyman Services') {
            const subType = getHandymanSubType(request)
            // Unknown sub-type: allow it only when nothing is paused.
            if (!subType) return !(settings.pausedSubTypes || []).length
            return !isSubTypePaused('Handyman Services', subType)
        }

        return true
    }

    return { isShownOnHome, getPausedMessage, isSubTypePaused, canConfirmRequest, getPaymentFlow }
}
